// src/components/common/aia/TourHighlight.js
import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const TourHighlight = ({ target, position = 'bottom', active, interactive = false, onOverlayClick }) => {
    const [highlightRect, setHighlightRect] = useState(null);

    useEffect(() => {
        if (!active || !target) {
            setHighlightRect(null);
            return;
        }

        let syncTimer = null;

        const isOutOfViewport = (rect) =>
            rect.top < 8 ||
            rect.left < 8 ||
            rect.bottom > window.innerHeight - 8 ||
            rect.right > window.innerWidth - 8;

        // Find the target element
        const findTarget = (shouldScroll = false) => {
            const element = document.querySelector(target);
            if (element) {
                const rect = element.getBoundingClientRect();

                if (shouldScroll && isOutOfViewport(rect)) {
                    setHighlightRect(null);
                    // Move target into view first, then measure and highlight.
                    element.scrollIntoView({
                        behavior: 'auto',
                        block: 'center',
                        inline: 'center'
                    });
                    if (syncTimer) clearTimeout(syncTimer);
                    syncTimer = setTimeout(() => {
                        const afterScrollRect = element.getBoundingClientRect();
                        setHighlightRect(afterScrollRect);
                    }, 60);
                    return;
                }

                setHighlightRect(rect);
            } else {
                setHighlightRect(null);
            }
        };

        findTarget(true);

        // Recalculate on scroll/resize
        const handleResize = () => findTarget();
        window.addEventListener('scroll', handleResize, true);
        window.addEventListener('resize', handleResize);

        // Also recalculate periodically in case of content changes
        const interval = setInterval(findTarget, 500);

        return () => {
            window.removeEventListener('scroll', handleResize, true);
            window.removeEventListener('resize', handleResize);
            clearInterval(interval);
            if (syncTimer) clearTimeout(syncTimer);
        };
    }, [target, active]);

    // Calculate spotlight position
    const spotlight = highlightRect ? {
        x: highlightRect.left - 8,
        y: highlightRect.top - 8,
        width: highlightRect.width + 16,
        height: highlightRect.height + 16,
        radius: 12
    } : null;

    // Calculate ring position (larger than spotlight)
    const ring = highlightRect ? {
        x: highlightRect.left - 16,
        y: highlightRect.top - 16,
        width: highlightRect.width + 32,
        height: highlightRect.height + 32
    } : null;

    // Build four transparent click-capture panes that tile AROUND the spotlight
    // hole. Clicks on these panes (i.e. outside the target) dismiss/pause the
    // tour. The hole itself is intentionally left uncovered so that — for an
    // interactive step — the real target element receives the click (which also
    // lets the tour's capture-phase listener detect it and advance). For a
    // non-interactive step we drop a transparent blocker over the hole so the
    // user advances with Next instead of accidentally navigating away.
    const vw = typeof window !== 'undefined' ? window.innerWidth : 0;
    const vh = typeof window !== 'undefined' ? window.innerHeight : 0;
    const holeRight = spotlight ? spotlight.x + spotlight.width : 0;
    const holeBottom = spotlight ? spotlight.y + spotlight.height : 0;
    const panes = spotlight ? [
        { left: 0, top: 0, width: vw, height: Math.max(0, spotlight.y) },                       // top
        { left: 0, top: holeBottom, width: vw, height: Math.max(0, vh - holeBottom) },          // bottom
        { left: 0, top: spotlight.y, width: Math.max(0, spotlight.x), height: spotlight.height },// left
        { left: holeRight, top: spotlight.y, width: Math.max(0, vw - holeRight), height: spotlight.height }, // right
    ] : [];

    return (
        <AnimatePresence>
            {active && spotlight && (
                <>
                    {/* Dark overlay with a real cut-out hole over the target (SVG mask).
                        pointer-events:none so it never blocks clicks. */}
                    <motion.svg
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-[9998] pointer-events-none"
                        style={{ width: '100vw', height: '100vh' }}
                    >
                        <defs>
                            <mask id="spotlight-mask">
                                <rect width="100%" height="100%" fill="white" />
                                <rect
                                    x={spotlight.x}
                                    y={spotlight.y}
                                    width={spotlight.width}
                                    height={spotlight.height}
                                    rx={spotlight.radius}
                                    fill="black"
                                />
                            </mask>
                        </defs>
                        <rect
                            width="100%"
                            height="100%"
                            fill="rgba(17, 24, 39, 0.7)"
                            mask="url(#spotlight-mask)"
                        />
                    </motion.svg>

                    {/* Click-capture panes around the hole — clicking outside the
                        target dismisses the tour. The hole is left open. */}
                    {panes.map((p, i) => (
                        <div
                            key={i}
                            className="fixed z-[9998] cursor-pointer"
                            style={{ left: p.left, top: p.top, width: p.width, height: p.height }}
                            onClick={onOverlayClick}
                        />
                    ))}

                    {/* Non-interactive steps: block target clicks so the user
                        advances with Next rather than navigating away mid-tour. */}
                    {!interactive && (
                        <div
                            className="fixed z-[9998]"
                            style={{ left: spotlight.x, top: spotlight.y, width: spotlight.width, height: spotlight.height }}
                            onClick={(e) => e.stopPropagation()}
                        />
                    )}

                    {/* Animated ring around highlighted element */}
                    <motion.div
                        initial={{ scale: 0.8, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        exit={{ scale: 0.8, opacity: 0 }}
                        className="fixed z-[9998] pointer-events-none rounded-2xl border-4 border-indigo-500 shadow-2xl shadow-indigo-500/50"
                        style={{
                            left: ring.x,
                            top: ring.y,
                            width: ring.width,
                            height: ring.height,
                        }}
                    >
                        {/* Pulsing effect */}
                        <motion.div
                            className="absolute inset-0 rounded-2xl border-2 border-indigo-400"
                            animate={{
                                scale: [1, 1.05, 1],
                                opacity: [0.5, 0.8, 0.5]
                            }}
                            transition={{
                                duration: 2,
                                repeat: Infinity,
                                ease: 'easeInOut'
                            }}
                        />
                    </motion.div>

                    {/* Arrow pointer pointing to highlighted element */}
                    <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="fixed z-[9999] pointer-events-none"
                        style={{
                            left: spotlight.x + spotlight.width / 2 - 12,
                            top: position === 'top' ? spotlight.y - 40 : spotlight.y + spotlight.height + 16,
                        }}
                    >
                        <div className={`w-6 h-6 bg-white dark:bg-gray-800 rounded-full shadow-lg flex items-center justify-center ${
                            position === 'top' ? 'rotate-180' : ''
                        }`}>
                            <div className="w-0 h-0 border-l-8 border-l-transparent border-r-8 border-r-transparent border-t-8 border-t-indigo-500" />
                        </div>
                    </motion.div>
                </>
            )}
        </AnimatePresence>
    );
};

export default TourHighlight;
